import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import ReactModal from 'react-modal';

const toOrderSnapshot = (list) =>
  (Array.isArray(list) ? list : []).map((p) => p.name);

const NotifySortModal = ({
  isOpen,
  onClose,
  notifyList,
  setNotifyList,
  shiftList,
  notifyDate,
  shiftType,
  fetchCurrentNotifyInfo,
  handleClearNotifyProgress,
  handleResetNotifyProgress,
}) => {
  const safeNotifyList = Array.isArray(notifyList) ? notifyList : [];
  const [initialOrder, setInitialOrder] = useState([]);
  const [sortExisted, setSortExisted] = useState(false);

  const currentOrder = useMemo(() => toOrderSnapshot(safeNotifyList), [safeNotifyList]);
  const hasChanges = useMemo(
    () => JSON.stringify(currentOrder) !== JSON.stringify(initialOrder),
    [currentOrder, initialOrder]
  );
  const saveButtonLabel = sortExisted ? '更新' : '儲存';

  // ⏳ 自動讀取該班該月的排序
  useEffect(() => {
    if (!isOpen) return;

    const fetchSavedOrder = async () => {
      try {
        const res = await fetch(
          `/api/notify-monthly-order?month=${moment(notifyDate).format('YYYY-MM')}&shiftType=${shiftType}`
        );
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const snapshot = toOrderSnapshot(list);
        setNotifyList(list);
        setInitialOrder(snapshot);
        setSortExisted(snapshot.length > 0);
      } catch (err) {
        console.error('❌ 載入通知順序失敗', err);
        setNotifyList([]);
        setInitialOrder([]);
        setSortExisted(false);
      }
    };

    fetchSavedOrder();
  }, [isOpen, notifyDate, shiftType, setNotifyList]);

  // 🧮 自動過濾未排定名單
  const notifyNames = safeNotifyList.map(p => p.name);
  const filteredAvailableList = shiftList.filter(
    p => !notifyNames.includes(p.name)
  );


  const handleAddToNotify = (person) => {
    if (safeNotifyList.find(p => p.id === person.id)) return;
    setNotifyList([
      ...safeNotifyList,
      {
        id: person.id,
        name: person.name,
        lineId: person.lineId || `U${person.id}`,
        hasSubmitted: false,
      },
    ]);
  };

  const handleRemove = (id) => {
    setNotifyList(prev => prev.filter(p => p.id !== id));
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newList = [...safeNotifyList];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setNotifyList(newList);
  };

  const moveDown = (index) => {
    if (index === safeNotifyList.length - 1) return;
    const newList = [...safeNotifyList];
    [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
    setNotifyList(newList);
  };

  const handleSubmitNotifyOrder = async () => {
    if (!notifyDate || !shiftType) {
      alert('❗請確認班別、月份與通知清單');
      return;
    }

    const monthStr = moment(notifyDate).format('YYYY-MM');

    if (safeNotifyList.length === 0) {
      const confirmed = window.confirm(`確定要清除 ${monthStr} ${shiftType} 班的通知排序嗎？`);
      if (!confirmed) return;

      try {
        const res = await fetch('/api/notify-monthly-order', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ month: monthStr, shiftType }),
        });

        const result = await res.json();
        if (result.success) {
          onClose();
          fetchCurrentNotifyInfo();
        } else {
          alert(`❌ 清除失敗：${result.message}`);
        }
      } catch (err) {
        console.error('❌ 清除排序失敗:', err);
        alert('系統錯誤，請稍後再試');
      }

      return;
    }

    const payload = {
      month: monthStr,
      shiftType,
      sequence: safeNotifyList.map((p, i) => ({
        id: p.name,
        order: i + 1,
      })),
    };

    try {
      const res = await fetch('/api/notify-monthly-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (result.success) {
        const action = sortExisted ? '更新' : '儲存';
        alert(`✅ 通知順序已${action}（共 ${result.inserted} 人）`);
        setInitialOrder(currentOrder);
        setSortExisted(currentOrder.length > 0);
        onClose();
        fetchCurrentNotifyInfo();
      } else {
        alert(`❌ ${saveButtonLabel}失敗`);
      }
    } catch (err) {
      console.error('❌ 儲存錯誤', err);
      alert('❌ 儲存失敗（伺服器錯誤）');
    }
  };


  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="bg-white p-4 rounded-lg w-[90%] max-w-5xl mx-auto mt-20 shadow"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
    >
      <h2 className="text-center text-xl font-bold mb-4 text-teal-800">
        {moment(notifyDate).format('YYYY-MM')} {shiftType}班通知排序設定
      </h2>

      <div className="grid grid-cols-[1fr_2fr] gap-6">
        {/* 左側：班別人員清單 */}
        <div className="text-sm">
          <ul className="space-y-2 max-h-80 overflow-auto border p-2 rounded border-teal-200 text-center">
            {filteredAvailableList.map((person) => (
              <li
                key={person.id}
                onClick={() => handleAddToNotify(person)}
                className="p-2 rounded bg-gray-100 hover:bg-teal-100 cursor-pointer shadow-sm"
              >
                {person.name}
              </li>
            ))}
          </ul>
        </div>

        {/* 右側：通知順序清單 */}
        <div>
          <p className="text-teal-700 font-semibold text-sm">通知順序(箭頭調順序)</p>
          <ul className="space-y-2 max-h-80 overflow-auto p-1 rounded border-teal-200 text-base">
            {safeNotifyList.map((person, index) => (
              <li
                key={person.id}
                className="p-1 rounded bg-teal-100 shadow flex justify-between items-center"
              >
                <div className="flex items-center gap-2">
                  <span className="font-bold">{index + 1}.</span>
                  <span>{person.name}</span>
                </div>
                <div className="flex gap-4">
                  <button onClick={() => moveUp(index)}>⬆️</button>
                  <button onClick={() => moveDown(index)}>⬇️</button>
                  <button onClick={() => handleRemove(person.id)} className="text-red-500">✖</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center gap-2">
        <div className="flex gap-2">
          <button
            onClick={handleResetNotifyProgress}
            className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1 rounded text-sm"
            title="保留排序，從第 1 位重新開始測試"
          >
            重置通知進度
          </button>
          <button
            onClick={handleClearNotifyProgress}
            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
            title="刪除排序與進度，需重新設定"
          >
            清空排序與進度
          </button>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-3 py-1 bg-gray-300 rounded">取消</button>
          {hasChanges && (
            <button
              onClick={handleSubmitNotifyOrder}
              className="px-3 py-1 bg-teal-600 text-white rounded"
            >
              {saveButtonLabel}
            </button>
          )}
        </div>
      </div>
    </ReactModal>
  );
};

export default NotifySortModal;
